import {
  FormEventHandler,
  ReactNode,
  useEffect,
  useId,
  useSyncExternalStore,
} from "react";
import { Modal } from "flowbite-react";
import type { ModalProps } from "flowbite-react";
import { twMerge } from "tailwind-merge";

// Flowbite's `dismissible` is not stacking-aware, so track open modals and keep only the topmost dismissible.
const openModalStack: string[] = [];
const stackListeners = new Set<() => void>();
const notifyStackChange = () =>
  stackListeners.forEach((listener) => listener());
const subscribeToStack = (listener: () => void) => {
  stackListeners.add(listener);
  return () => {
    stackListeners.delete(listener);
  };
};
const getTopModalId = () =>
  openModalStack.length ? openModalStack[openModalStack.length - 1] : null;

export type ModalShellProps = {
  show: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  className?: string;
  rootClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  showCloseButton?: boolean;
  bareCloseButton?: boolean;
  size?: ModalProps["size"];
  position?: ModalProps["position"];
  dismissible?: ModalProps["dismissible"];
  initialFocus?: ModalProps["initialFocus"];
  root?: ModalProps["root"];
  "aria-label"?: string;
};

const OVERLAY =
  "fixed inset-x-0 top-0 z-50 h-dvh overflow-y-auto overflow-x-hidden md:inset-0 md:h-full";

const SURFACE = "relative flex max-h-[90dvh] flex-col rounded-lg bg-white";

const HEADER =
  "flex items-start justify-between gap-4 rounded-t-lg border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4";

const BODY = "relative flex-1 overflow-auto p-4 sm:p-6";

const FOOTER =
  "flex flex-wrap items-center justify-end gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6";

const BARE_HEADER = "flex justify-end rounded-t-lg border-0 px-2 pt-2 pb-0";

export const ModalShell = ({
  show,
  onClose,
  title,
  subtitle,
  header,
  footer,
  children,
  onSubmit,
  className,
  rootClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  showCloseButton = true,
  bareCloseButton = false,
  size = "2xl",
  position,
  dismissible,
  initialFocus,
  root,
  ...rest
}: ModalShellProps) => {
  const modalId = useId();

  useEffect(() => {
    if (!show) return;
    openModalStack.push(modalId);
    notifyStackChange();
    return () => {
      const index = openModalStack.lastIndexOf(modalId);
      if (index !== -1) openModalStack.splice(index, 1);
      notifyStackChange();
    };
  }, [show, modalId]);

  const topModalId = useSyncExternalStore(
    subscribeToStack,
    getTopModalId,
    getTopModalId
  );
  const isTopmost = show && topModalId === modalId;
  const effectiveDismissible = dismissible ? isTopmost : dismissible;

  const hasHeader = Boolean(title) || Boolean(subtitle);
  const isBare = !hasHeader && bareCloseButton;
  const headerBase = twMerge(isBare ? BARE_HEADER : HEADER, headerClassName);

  const heading = (
    <div className="min-w-0">
      {title ? (
        <h3 className="break-words text-lg font-semibold text-gray-900">
          {title}
        </h3>
      ) : null}
      {subtitle ? (
        <p className="mt-0.5 break-words text-sm text-gray-500">{subtitle}</p>
      ) : null}
    </div>
  );

  const body = (
    <>
      <Modal.Body className={bodyClassName}>{children}</Modal.Body>
      {footer ? (
        <Modal.Footer className={footerClassName}>{footer}</Modal.Footer>
      ) : null}
    </>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      size={size}
      position={position}
      dismissible={effectiveDismissible}
      initialFocus={initialFocus}
      root={root}
      className={rootClassName}
      theme={{
        root: { base: OVERLAY },
        content: { inner: twMerge(SURFACE, "shadow-lg", className) },
        header: {
          base: headerBase,
          title: "min-w-0 flex-1",
          close: {
            base: "ml-auto inline-flex items-center rounded-md bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
          },
        },
        body: { base: BODY },
        footer: { base: FOOTER },
      }}
      {...rest}
    >
      {header}
      {isBare && <Modal.Header as="div" />}
      {hasHeader &&
        (showCloseButton ? (
          <Modal.Header as="div">{heading}</Modal.Header>
        ) : (
          <div className={headerBase}>{heading}</div>
        ))}
      {onSubmit ? (
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          {body}
        </form>
      ) : (
        body
      )}
    </Modal>
  );
};

export default ModalShell;
